/**
 * API client for FormFit Admin.
 * Uses credentials: 'include' so cookies (access/refresh tokens) are sent and received.
 */

const BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

let onUnauthorized = () => {};

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

async function request(path, options = {}, retried = false) {
  const url = `${BASE_URL}${path}`;
  const config = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };
  if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
    config.body = JSON.stringify(options.body);
  }
  const res = await fetch(url, config);

  if (res.status === 401 && !retried) {
    const refreshed = await refresh();
    if (refreshed) return request(path, options, true);
    onUnauthorized();
    const err = new Error('Unauthorized');
    err.status = 401;
    throw err;
  }

  if (!res.ok) {
    const err = new Error(res.statusText || 'Request failed');
    err.status = res.status;
    try {
      err.body = await res.json();
    } catch {
      err.body = await res.text();
    }
    throw err;
  }

  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return res.json();
  }
  return res.text();
}

/**
 * POST /v1/admin/auth/login
 * Request body: { email, password }
 * Response: { admin } — tokens are set in cookies by the server.
 */
export async function login(email, password) {
  return request('/v1/admin/auth/login', {
    method: 'POST',
    body: { email, password },
  });
}

/**
 * POST /v1/admin/auth/logout
 * No body; sends cookies. Server responds with cleared cookies.
 */
export async function logout() {
  try {
    await fetch(`${BASE_URL}/v1/admin/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
  } catch {
    // Ignore errors; we still clear local state
  }
}

/**
 * POST /v1/admin/auth/refresh
 * No body; uses cookies. Returns { admin } and may set new cookies.
 */
export async function refresh() {
  try {
    const res = await fetch(`${BASE_URL}/v1/admin/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

/**
 * GET /v1/admin/dashboard
 * No params. Returns { templatesCount, toolsCount }.
 */
export async function getDashboard() {
  return request('/v1/admin/dashboard', { method: 'GET' });
}

/**
 * GET /v1/admin/templates?skip=0&limit=10
 * Returns { totalCount, skipped, limit, listOfItems }.
 */
export async function getTemplates(skip = 0, limit = 10) {
  const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
  return request(`/v1/admin/templates?${params}`, { method: 'GET' });
}

/**
 * GET /v1/admin/templates/:id
 * Returns a single template.
 */
export async function getTemplate(id) {
  return request(`/v1/admin/templates/${id}`, { method: 'GET' });
}

/**
 * POST /v1/admin/templates
 * Body: { title, description?, instructions?, requirements?, tools? }
 */
export async function createTemplate(body) {
  return request('/v1/admin/templates', { method: 'POST', body });
}

/**
 * PATCH /v1/admin/templates/:id
 * Body: { title?, description?, instructions?, requirements?, tools? } — partial update.
 */
export async function updateTemplate(id, body) {
  return request(`/v1/admin/templates/${id}`, { method: 'PATCH', body });
}

/**
 * DELETE /v1/admin/templates/:id
 */
export async function deleteTemplate(id) {
  return request(`/v1/admin/templates/${id}`, { method: 'DELETE' });
}

/**
 * GET /v1/admin/tools?skip=0&limit=100
 * Returns { totalCount, skipped, limit, listOfItems }.
 */
export async function getTools(skip = 0, limit = 100) {
  const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
  return request(`/v1/admin/tools?${params}`, { method: 'GET' });
}
